import { encode, decode } from './protobuf';
import schema, { SignalTarget, TrackType, TrackSource, DisconnectReason } from './signalSchema';

const PROTOCOL_VERSION = 9;
const TRACK_PUBLISH_TIMEOUT_MS = 10000;

// One participant's connection to a LiveKit room, speaking LiveKit's signaling protocol directly (protobuf
// over a WebSocket) instead of through its client SDK. LiveKit uses two peer connections: the publisher, which
// this side offers, and the subscriber, which the server offers. See
// https://docs.livekit.io/reference/internals/client-protocol/.
export default class LiveKitConnection {
	constructor(url, { autoSubscribe = false, onParticipants, onTrack, onClose } = {}) {
		this._url = url;
		this._autoSubscribe = autoSubscribe;
		this._onParticipants = onParticipants;
		this._onTrack = onTrack;
		this._onClose = onClose;
		this._ws = null;
		this._iceServers = [];
		this._publisher = null;
		this._subscriber = null;
		this._pendingCandidates = { [SignalTarget.PUBLISHER]: [], [SignalTarget.SUBSCRIBER]: [] };
		this._pendingTracks = new Map(); // cid -> { resolve, reject, timer }
		this._pingTimer = null;
		this._lastMessageAt = 0;
		this._closed = false;
		this.participant = null;
	}

	// Resolves with the JoinResponse once the server has accepted the token.
	connect(token) {
		return new Promise((resolve, reject) => {
			const params = new URLSearchParams({
				access_token: token,
				auto_subscribe: this._autoSubscribe ? '1' : '0',
				sdk: 'js',
				protocol: String(PROTOCOL_VERSION),
			});
			const ws = new WebSocket(`${this._url.replace(/\/+$/, '')}/rtc?${params}`);
			ws.binaryType = 'arraybuffer';
			this._ws = ws;
			let joined = false;
			ws.onmessage = e => {
				if(!(e.data instanceof ArrayBuffer)) return;
				let message;
				try {
					message = decode(schema, 'SignalResponse', new Uint8Array(e.data));
				} catch(err) {
					console.error('LiveKit: could not decode signal message', err);
					return;
				}
				this._lastMessageAt = Date.now();
				if(message.join) {
					joined = true;
					this._handleJoin(message.join);
					resolve(message.join);
				} else {
					this._handleMessage(message);
				}
			};
			ws.onerror = () => {};
			ws.onclose = () => {
				if(!joined) reject(new Error('LiveKit: connection closed before joining'));
				this.close();
			};
		});
	}

	// Publishes every track of the stream; resolves once the publisher connection has been negotiated.
	async publish(stream, { screen = false } = {}) {
		const senders = {};
		const tracks = stream.getTracks();
		await Promise.all(tracks.map(track => this._addTrack(track, screen)));
		const pc = this._getPublisher();
		for(const track of tracks) {
			if(track.kind === 'video') track.contentHint = 'motion';
			senders[track.kind] = pc.addTransceiver(track, { direction: 'sendonly', streams: [ stream ] }).sender;
		}
		await this._negotiatePublisher();
		return senders;
	}

	subscribe(participantSid, trackSids, subscribe = true) {
		if(!trackSids.length) return;
		this._send({ subscription: { subscribe, participant_tracks: [ { participant_sid: participantSid, track_sids: trackSids } ] } });
	}

	close() {
		if(this._closed) return;
		this._closed = true;
		clearInterval(this._pingTimer);
		for(const { reject, timer } of this._pendingTracks.values()) {
			clearTimeout(timer);
			reject(new Error('LiveKit: connection closed'));
		}
		this._pendingTracks.clear();
		if(this._ws) {
			if(this._ws.readyState === WebSocket.OPEN) this._send({ leave: { reason: DisconnectReason.CLIENT_INITIATED } });
			this._ws.onclose = null;
			this._ws.onmessage = null;
			this._ws.close();
			this._ws = null;
		}
		this._publisher?.close();
		this._subscriber?.close();
		this._publisher = null;
		this._subscriber = null;
		this._onClose?.();
	}

	_send(request) {
		if(this._ws?.readyState === WebSocket.OPEN) this._ws.send(encode(schema, 'SignalRequest', request));
	}

	_handleJoin(join) {
		this.participant = join.participant;
		this._iceServers = (join.ice_servers || []).map(s => ({ urls: s.urls, username: s.username || undefined, credential: s.credential || undefined }));
		this._onParticipants?.(join.other_participants || []);
		this._startPings(join.ping_interval, join.ping_timeout);
	}

	_handleMessage(message) {
		if(message.offer) this._handleSubscriberOffer(message.offer);
		else if(message.answer) this._publisher?.setRemoteDescription({ type: 'answer', sdp: message.answer.sdp }).then(() => this._flushCandidates(SignalTarget.PUBLISHER)).catch(e => console.error('LiveKit: publisher answer', e));
		else if(message.trickle) this._handleTrickle(message.trickle);
		else if(message.update) this._onParticipants?.(message.update.participants || []);
		else if(message.track_published) this._resolveTrack(message.track_published);
		else if(message.leave) this.close();
		else if(message.request_response && message.request_response.reason) console.error('LiveKit: request refused', message.request_response);
	}

	_startPings(intervalSeconds, timeoutSeconds) {
		if(!intervalSeconds) return;
		const timeoutMs = (timeoutSeconds || intervalSeconds * 3) * 1000;
		this._pingTimer = setInterval(() => {
			if(Date.now() - this._lastMessageAt > timeoutMs) {
				this.close();
				return;
			}
			this._send({ ping: Date.now() });
		}, intervalSeconds * 1000);
	}

	_addTrack(track, screen) {
		return new Promise((resolve, reject) => {
			const timer = setTimeout(() => {
				this._pendingTracks.delete(track.id);
				reject(new Error('LiveKit: track was not published'));
			}, TRACK_PUBLISH_TIMEOUT_MS);
			this._pendingTracks.set(track.id, { resolve, reject, timer });
			const settings = track.getSettings?.() || {};
			const video = track.kind === 'video';
			this._send({ add_track: {
				cid: track.id,
				name: track.kind,
				type: video ? TrackType.VIDEO : TrackType.AUDIO,
				width: video ? settings.width : undefined,
				height: video ? settings.height : undefined,
				muted: !track.enabled,
				source: screen
					? (video ? TrackSource.SCREEN_SHARE : TrackSource.SCREEN_SHARE_AUDIO)
					: (video ? TrackSource.CAMERA : TrackSource.MICROPHONE),
			} });
		});
	}

	_resolveTrack({ cid, track }) {
		const pending = this._pendingTracks.get(cid);
		if(!pending) return;
		clearTimeout(pending.timer);
		this._pendingTracks.delete(cid);
		pending.resolve(track);
	}

	_createPeerConnection(target) {
		const pc = new RTCPeerConnection({ iceServers: this._iceServers });
		pc.onicecandidate = e => {
			if(!e.candidate) return;
			this._send({ trickle: { candidateInit: JSON.stringify(e.candidate.toJSON()), target } });
		};
		pc.onconnectionstatechange = () => {
			if(pc.connectionState === 'failed') this.close();
		};
		return pc;
	}

	_getPublisher() {
		if(!this._publisher) this._publisher = this._createPeerConnection(SignalTarget.PUBLISHER);
		return this._publisher;
	}

	async _negotiatePublisher() {
		const pc = this._getPublisher();
		const offer = await pc.createOffer();
		await pc.setLocalDescription(offer);
		this._send({ offer: { type: 'offer', sdp: offer.sdp } });
	}

	async _handleSubscriberOffer(offer) {
		try {
			if(!this._subscriber) {
				this._subscriber = this._createPeerConnection(SignalTarget.SUBSCRIBER);
				this._subscriber.ontrack = e => {
					// Lowest latency, as OmeReceiver does.
					if('playoutDelayHint' in e.receiver) e.receiver.playoutDelayHint = 0;
					if('jitterBufferTarget' in e.receiver) e.receiver.jitterBufferTarget = 0;
					this._onTrack?.(e);
				};
			}
			const pc = this._subscriber;
			await pc.setRemoteDescription({ type: 'offer', sdp: offer.sdp });
			await this._flushCandidates(SignalTarget.SUBSCRIBER);
			const answer = await pc.createAnswer();
			await pc.setLocalDescription(answer);
			this._send({ answer: { type: 'answer', sdp: answer.sdp, id: offer.id } });
		} catch(e) {
			console.error('LiveKit: subscriber offer', e);
			this.close();
		}
	}

	_handleTrickle({ candidateInit, target }) {
		let candidate;
		try {
			candidate = JSON.parse(candidateInit);
		} catch(e) {
			return;
		}
		const pc = target === SignalTarget.SUBSCRIBER ? this._subscriber : this._publisher;
		// Candidates are dropped unless the remote description is already set.
		if(!pc?.remoteDescription) {
			this._pendingCandidates[target ?? SignalTarget.PUBLISHER].push(candidate);
			return;
		}
		pc.addIceCandidate(candidate).catch(() => {});
	}

	async _flushCandidates(target) {
		const pc = target === SignalTarget.SUBSCRIBER ? this._subscriber : this._publisher;
		const candidates = this._pendingCandidates[target];
		this._pendingCandidates[target] = [];
		for(const candidate of candidates) await pc.addIceCandidate(candidate).catch(() => {});
	}
}
